import { useEffect, useRef, useState } from "react";
import api, { apiErr, fmtDate } from "@/lib/api";
import { DELIVERY_STATUSES } from "@/lib/constants";
import { PageHeader, Loader, EmptyState } from "@/components/ui-kit";
import PdfPreviewDialog from "@/components/PdfPreviewDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Plus, PackageCheck, MoreVertical, Pencil, Trash2, Loader2, Download, PenLine, Eye, Upload, FileCheck } from "lucide-react";
import { SignaturePad } from "@/components/SignaturePad";
import { toast } from "sonner";

export default function Deliveries() {
  const [items, setItems] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const [sign, setSign] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploadingId, setUploadingId] = useState("");
  const proofRef = useRef();

  const load = () => api.get("/deliveries").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/invoices").then((r) => setInvoices(r.data.filter((i) => i.statut !== "Annulée"))).catch(() => {});
    api.get("/employees").then((r) => setEmployees(r.data)).catch(() => {});
    api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
    api.get("/client-projects").then((r) => setProjects(r.data)).catch(() => {});
  }, []);

  const openNew = () => { setForm({ invoice_id: "", client_name: "", adresse: "", livreur: "", livreur_id: "", vehicule: "", vehicle_id: "", client_project_id: "", date_prevue: new Date().toISOString().slice(0, 10), date_reelle: "", statut: "Préparation", commentaires: "", items: [] }); setOpen(true); };
  const openEdit = (d) => { setForm({ ...d }); setOpen(true); };

  const pickInvoice = (id) => {
    const inv = invoices.find((x) => x.id === id);
    setForm((p) => ({ ...p, invoice_id: id, client_name: inv?.client_name || p.client_name, items: inv?.items || [] }));
  };
  const pickLivreur = (id) => { const e = employees.find((x) => x.id === id); setForm((p) => ({ ...p, livreur_id: id, livreur: e ? (e.nom_complet || `${e.prenom || ""} ${e.nom || ""}`.trim() || e.nom) : p.livreur })); };
  const pickVehicle = (id) => { const v = vehicles.find((x) => x.id === id); setForm((p) => ({ ...p, vehicle_id: id, vehicule: v ? `${v.immatriculation}${v.marque ? " · " + v.marque : ""}` : p.vehicule })); };
  const pickProject = (id) => { const pr = projects.find((x) => x.id === id); setForm((p) => ({ ...p, client_project_id: id, client_project_name: pr ? pr.nom : "" })); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.client_name) { toast.error("Client requis"); return; }
    setSaving(true);
    try {
      if (form.id) await api.put(`/deliveries/${form.id}`, form);
      else await api.post("/deliveries", form);
      toast.success("Livraison enregistrée"); setOpen(false); load();
    } catch (err) { toast.error(apiErr(err)); } finally { setSaving(false); }
  };
  const setStatus = async (id, statut) => { try { await api.post(`/deliveries/${id}/status`, { statut }); toast.success("Statut mis à jour"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const remove = async (id) => { if (!window.confirm("Supprimer cette livraison ?")) return; await api.delete(`/deliveries/${id}`); toast.success("Supprimé"); load(); };
  const openSign = (d) => setSign({ ...d, signataire: d.signataire || "", signature_data: d.signature_data || "" });
  const saveSign = async () => { try { await api.put(`/deliveries/${sign.id}`, sign); toast.success("Signature enregistrée"); setSign(null); load(); } catch (e) { toast.error(apiErr(e)); } };

  const triggerProof = (d) => { setUploadingId(d.id); proofRef.current.click(); };
  const onProofFile = async (e) => {
    const f = e.target.files[0]; e.target.value = "";
    if (!f || !uploadingId) return;
    const fd = new FormData(); fd.append("file", f);
    try { await api.post(`/deliveries/${uploadingId}/proof`, fd); toast.success("Preuve de réception enregistrée"); load(); }
    catch (err) { toast.error(apiErr(err)); } finally { setUploadingId(""); }
  };
  const viewProof = (d) => { setPreview({ path: `/deliveries/${d.id}/proof`, filename: d.preuve_name || "preuve", title: "Preuve de réception" }); };

  return (
    <div>
      <PageHeader title="Livraisons" subtitle="Bons de livraison professionnels · livreur, véhicule, preuve & signature">
        <Button onClick={openNew} data-testid="add-delivery" className="bg-brand-600 hover:bg-brand-700 text-white"><Plus className="h-4 w-4 mr-1.5" />Nouvelle livraison</Button>
      </PageHeader>

      {items === null ? <Loader /> : items.length === 0 ? (
        <EmptyState icon={PackageCheck} title="Aucune livraison" subtitle="Créez une livraison à partir d'une facture validée." />
      ) : (
        <div className="rounded-xl border bg-white overflow-hidden"><div className="overflow-x-auto scrollbar-thin">
          <Table>
            <TableHeader><TableRow className="bg-slate-50"><TableHead>N°</TableHead><TableHead>Client</TableHead><TableHead>Livreur</TableHead><TableHead>Véhicule</TableHead><TableHead>Prévue</TableHead><TableHead>Statut</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {items.map((d) => (
                <TableRow key={d.id} data-testid={`delivery-row-${d.id}`} className="hover:bg-slate-50">
                  <TableCell className="font-mono text-xs text-slate-500">{d.numero}</TableCell>
                  <TableCell className="font-semibold">{d.client_name}</TableCell>
                  <TableCell className="text-slate-600 text-sm">{d.livreur || "—"}</TableCell>
                  <TableCell className="text-slate-600 text-sm">{d.vehicule || "—"}</TableCell>
                  <TableCell className="text-sm">{d.date_prevue ? fmtDate(d.date_prevue) : "—"}</TableCell>
                  <TableCell>
                    <Select value={d.statut} onValueChange={(v) => setStatus(d.id, v)}>
                      <SelectTrigger className="h-8 w-52" data-testid={`delivery-status-${d.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{DELIVERY_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`delivery-menu-${d.id}`}><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEdit(d)}><Pencil className="h-4 w-4 mr-2" />Détails</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setPreview({ path: `/deliveries/${d.id}/pdf`, filename: `bon_livraison_${d.numero}.pdf`, title: "Aperçu du bon de livraison" })} data-testid={`delivery-preview-${d.id}`}><Eye className="h-4 w-4 mr-2" />Aperçu avant impression</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setPreview({ path: `/deliveries/${d.id}/pdf`, filename: `bon_livraison_${d.numero}.pdf`, title: "Bon de livraison" })} data-testid={`delivery-pdf-${d.id}`}><Download className="h-4 w-4 mr-2" />Bon de livraison PDF</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openSign(d)} data-testid={`delivery-sign-${d.id}`}><PenLine className="h-4 w-4 mr-2" />Signature de réception</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => triggerProof(d)} data-testid={`delivery-proof-upload-${d.id}`}><Upload className="h-4 w-4 mr-2" />Téléverser une preuve</DropdownMenuItem>
                        {d.preuve_url && <DropdownMenuItem onClick={() => viewProof(d)} data-testid={`delivery-proof-view-${d.id}`}><FileCheck className="h-4 w-4 mr-2" />Voir la preuve</DropdownMenuItem>}
                        <DropdownMenuItem onClick={() => remove(d.id)} className="text-rose-600"><Trash2 className="h-4 w-4 mr-2" />Supprimer</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div></div>
      )}

      <input ref={proofRef} type="file" accept="image/*,.pdf" hidden onChange={onProofFile} data-testid="delivery-proof-input" />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head">{form?.id ? `Livraison ${form.numero}` : "Nouvelle livraison"}</DialogTitle></DialogHeader>
          {form && (
            <form onSubmit={save} className="space-y-3">
              {!form.id && (
                <div>
                  <Label className="text-xs">Facture d'origine (validée)</Label>
                  <Select value={form.invoice_id} onValueChange={pickInvoice}>
                    <SelectTrigger className="mt-1" data-testid="delivery-invoice"><SelectValue placeholder="Choisir une facture (optionnel)" /></SelectTrigger>
                    <SelectContent>{invoices.map((i) => <SelectItem key={i.id} value={i.id}>{i.numero} · {i.client_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><Label className="text-xs">Client</Label><Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} required data-testid="delivery-client" className="mt-1" /></div>
                <div><Label className="text-xs">Projet client</Label>
                  <Select value={form.client_project_id || ""} onValueChange={pickProject}>
                    <SelectTrigger className="mt-1" data-testid="delivery-project"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                    <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.nom}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2"><Label className="text-xs">Adresse de livraison</Label><Input value={form.adresse} onChange={(e) => setForm({ ...form, adresse: e.target.value })} className="mt-1" /></div>
                <div><Label className="text-xs">Livreur (collaborateur)</Label>
                  <Select value={form.livreur_id || ""} onValueChange={pickLivreur}>
                    <SelectTrigger className="mt-1" data-testid="delivery-livreur"><SelectValue placeholder={form.livreur || "Sélectionner"} /></SelectTrigger>
                    <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.nom_complet || `${e.prenom || ""} ${e.nom || ""}`.trim() || e.nom}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Véhicule</Label>
                  <Select value={form.vehicle_id || ""} onValueChange={pickVehicle}>
                    <SelectTrigger className="mt-1" data-testid="delivery-vehicle"><SelectValue placeholder={form.vehicule || "Sélectionner"} /></SelectTrigger>
                    <SelectContent>{vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.immatriculation}{v.marque ? ` · ${v.marque}` : ""}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Date prévue</Label><Input type="date" value={form.date_prevue || ""} onChange={(e) => setForm({ ...form, date_prevue: e.target.value })} className="mt-1" /></div>
                <div><Label className="text-xs">Date réelle</Label><Input type="date" value={form.date_reelle || ""} onChange={(e) => setForm({ ...form, date_reelle: e.target.value })} className="mt-1" /></div>
                <div className="md:col-span-2"><Label className="text-xs">Statut</Label>
                  <Select value={form.statut} onValueChange={(v) => setForm({ ...form, statut: v })}><SelectTrigger className="mt-1" data-testid="delivery-statut"><SelectValue /></SelectTrigger>
                    <SelectContent>{DELIVERY_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
                <div className="md:col-span-2"><Label className="text-xs">Commentaires</Label><Textarea value={form.commentaires} onChange={(e) => setForm({ ...form, commentaires: e.target.value })} rows={2} className="mt-1" /></div>
                {form.id && form.preuve_url && <div className="md:col-span-2 text-xs text-emerald-700 flex items-center gap-1.5"><FileCheck className="h-4 w-4" />Preuve de réception jointe : {form.preuve_name}</div>}
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
                <Button type="submit" disabled={saving} data-testid="delivery-submit" className="bg-brand-600 hover:bg-brand-700 text-white">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!sign} onOpenChange={(o) => !o && setSign(null)}>
        <DialogContent className="max-w-md" onPointerDownOutside={(e) => e.preventDefault()} onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader><DialogTitle className="font-head">Signature de réception — {sign?.numero}</DialogTitle></DialogHeader>
          {sign && (
            <div className="space-y-3">
              <div><Label className="text-xs">Nom du réceptionnaire</Label><Input value={sign.signataire} onChange={(e) => setSign({ ...sign, signataire: e.target.value })} className="mt-1" data-testid="sign-name" /></div>
              <div><Label className="text-xs">Signature</Label><SignaturePad value={sign.signature_data} onChange={(v) => setSign({ ...sign, signature_data: v })} /></div>
              <DialogFooter><Button type="button" variant="outline" onClick={() => setSign(null)}>Annuler</Button><Button onClick={saveSign} data-testid="sign-save" className="bg-brand-600 hover:bg-brand-700 text-white">Enregistrer la signature</Button></DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <PdfPreviewDialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)} path={preview?.path} filename={preview?.filename} title={preview?.title} />
    </div>
  );
}
